import type { ReactElement, ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { Spinner } from '../components'
import { HOME_PATH } from '../lib/routes'
import { useSession } from './useSession'
import type { Section } from './useSession'

export type RequireSectionProps = {
  section: Section
  children: ReactNode
}

/**
 * The guard around the screens of one section (groups, assignments, users,
 * operations).
 *
 * ⚠️ Hiding a screen is NOT the protection. Every procedure behind these screens
 * declares its section in the API, and that is what actually refuses a person
 * whose role lacks it; this only keeps them from staring at a screen that
 * answers FORBIDDEN to everything, and sends them home when they type the URL by
 * hand. Home itself requires no section, so the bounce cannot loop.
 *
 * It sits INSIDE `RequireAuth`, so the session is normally already resolved —
 * the loading branch is for the case where this mounts on the same tick as the
 * first `auth.me`, and it must not redirect while the sections are unknown.
 */
export const RequireSection = ({ section, children }: RequireSectionProps): ReactElement => {
  const { isLoading, hasSection } = useSession()

  if (isLoading) {
    return <Spinner size="page" label="Comprobando permisos…" />
  }

  if (!hasSection(section)) {
    return <Navigate to={HOME_PATH} replace />
  }

  return <>{children}</>
}
